/**
 * MintSection
 *
 * Unified mint UI component for CollectionDetailPage.
 * Displays mint progress, price, eligibility, quantity selector, and mint button.
 *
 * Has two layout modes:
 * - Full (compact=false): Vertical layout
 * - Compact (compact=true): Horizontal banner layout for embedding in collection pages
 */

import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { Button } from '../../ui/components/Button';
import { LoadingSpinner } from '../ui/LoadingSpinner';
import { WalletSigningModal } from '../transactions/WalletSigningModal';
import type { SigningPayload } from '../../types/signing';
import { MintProgressBar } from './MintProgressBar';
import { MintCountdownTimer } from './MintCountdownTimer';
import { MintStatusModal } from './MintStatusModal';
import {
  FeatherCheck,
  FeatherAlertCircle,
  FeatherClock,
  FeatherMinus,
  FeatherPlus,
  FeatherCopy,
  FeatherCreditCard,
} from '@subframe/core';
import { formatDropsToXrp, type DropEdition, type Eligibility, type MintStatus, type UserMint } from '../../api/drops';
import { calculateMintServiceFee } from '../../lib/marketplace-constants';
import { EditionGallery } from './EditionGallery';

export interface UserAllowlistStatus {
  isOnAllowlist: boolean;
  isOnAllowlist2: boolean;
  mintLimit?: number;
  mintedCount?: number;
  mintLimit2?: number;
  mintedCount2?: number;
}

export interface LaunchProgress {
  total: number;
  minted: number;
  processing: number;
  available: number;
}

export interface ActiveLaunchForMint {
  launch_id: string;
  launch_slug: string;
  launch_status: string;
  mint_phase: 'allowlist' | 'allowlist_2' | 'public' | null;
  allowlist_enabled: boolean;
  allowlist_2_enabled: boolean;
  allowlist_start_at: Date | string | null;
  allowlist_2_start_at: Date | string | null;
  public_start_at: Date | string | null;
  end_at: Date | string | null;
  mint_price_drops: number | null;
  allowlist_mint_price_drops: number | null;
  allowlist_2_mint_price_drops: number | null;
  launch_total_supply: number | null;
  minted_count: number;
  transfer_fee: number;
  limit_per_wallet: number | null;
  service_fee_percent?: number;
  userAllowlistStatus?: UserAllowlistStatus | null;
  eligibility?: Eligibility | null;
  availableCount?: number;
  progress?: LaunchProgress;
  /** Launch type: 'standard' | 'composable' | 'edition' */
  collection_type?: string;
  /**
   * Minting is closed to the public — NFTs are obtained only by redeeming a
   * per-copy claim link. The backend rejects public mints for these
   * launches regardless of what the UI shows; this just renders the right story.
   * See docs/features/LAUNCH_CLAIM_TOKENS.md
   */
  claim_only?: boolean;
}

export interface MintSectionProps {
  /** Active launch data */
  launch: ActiveLaunchForMint;
  /** Whether the user is authenticated */
  isAuthenticated: boolean;
  /** Current quantity selected */
  quantity: number;
  /** Quantity change handler */
  onQuantityChange: (qty: number) => void;
  /** Loading state for mint button */
  mintLoading: boolean;
  /** Error message to display */
  mintError: string | null;
  /** Handler for mint button click */
  onMint: () => void;
  /** Pending mint (if any) */
  pendingMint: UserMint | null;
  /** Number of NFTs in pending reservation */
  pendingMintCount: number;
  /** Loading state for resume button */
  resumeLoading: boolean;
  /** Handler for resume button click */
  onResume: () => void;
  /** Handler to open cancel modal */
  onOpenCancelModal: () => void;
  /** Payment modal state */
  showPaymentModal: boolean;
  /** Payment payload for Xaman */
  paymentPayload: SigningPayload | null;
  /** Handler for payment modal close */
  onClosePaymentModal: () => void;
  /** Handler for payment success */
  onPaymentSuccess: (txid: string) => void;
  /** Handler for payment rejected */
  onPaymentRejected: () => void;
  /** Handler for payment expired */
  onPaymentExpired: () => void;
  /** Mint status for status modal */
  mintStatus: MintStatus | null;
  /** Whether to show mint status modal */
  showMintStatus: boolean;
  /** Handler to close mint status modal */
  onCloseMintStatus: () => void;
  /** Number of mint IDs being tracked */
  currentMintCount: number;
  /** Cancel modal state */
  showCancelModal: boolean;
  /** Cancel countdown timer value */
  cancelCountdown: number;
  /** Cancel loading state */
  cancelLoading: boolean;
  /** Handler to close cancel modal */
  onCloseCancelModal: () => void;
  /** Handler for cancel confirmation */
  onCancel: () => void;
  /** Optional: link to collection page */
  collectionSlug?: string;
  /** Optional: compact mode for embedding in other pages */
  compact?: boolean;
  /** Referral link info (shown inline when provided) */
  referral?: {
    referralCode: string;
    launchSlug: string;
    referralBps: number;
    mintPriceDrops: number;
    serviceFeePercent: number;
  };
  /** Edition drops: the artworks to pick from */
  editions?: DropEdition[] | null;
  /** Edition drops: currently selected artwork */
  selectedEditionId?: string | null;
  /** Edition drops: selection handler */
  onSelectEdition?: (editionId: string | null) => void;
}

/**
 * Format royalty percentage to show up to 2 decimal places only if needed
 * 8000 -> "8", 8800 -> "8.8", 8880 -> "8.88"
 */
function formatRoyalty(transferFee: number): string {
  const percentage = transferFee / 1000;
  if (percentage === Math.floor(percentage)) {
    return percentage.toString();
  }
  const oneDecimal = Math.round(percentage * 10) / 10;
  if (percentage === oneDecimal) {
    return percentage.toFixed(1);
  }
  return percentage.toFixed(2);
}

/**
 * Get countdown info for phase display
 *
 * Returns info about what to show in the countdown:
 * - If minting is NOT yet available: shows when next phase STARTS (neutral styling)
 * - If minting IS currently live: shows when current phase ENDS (success styling)
 */
function getPhaseCountdownInfo(
  allowlistStart: string | Date | null,
  publicStart: string | Date | null,
  allowlistEnabled: boolean,
  allowlist2Start?: string | Date | null,
  allowlist2Enabled?: boolean,
  mintPhase?: 'allowlist' | 'allowlist_2' | 'public' | null,
  endAt?: string | Date | null
): { isLive: boolean; countdownTo: Date | null; label: string } {
  const now = new Date();

  // If we're in an active mint phase, show when current phase ENDS
  if (mintPhase) {
    if (mintPhase === 'allowlist') {
      // In Phase 1 - countdown to Phase 2 or Public
      if (allowlist2Enabled && allowlist2Start) {
        const al2Start = new Date(allowlist2Start);
        if (now < al2Start) {
          return { isLive: true, countdownTo: al2Start, label: 'Allowlist Phase 1 ends in' };
        }
      }
      if (publicStart) {
        const pubStart = new Date(publicStart);
        if (now < pubStart) {
          return { isLive: true, countdownTo: pubStart, label: 'Allowlist ends in' };
        }
      }
    } else if (mintPhase === 'allowlist_2') {
      // In Phase 2 - countdown to Public
      if (publicStart) {
        const pubStart = new Date(publicStart);
        if (now < pubStart) {
          return { isLive: true, countdownTo: pubStart, label: 'Allowlist Phase 2 ends in' };
        }
      }
    } else if (mintPhase === 'public') {
      // In Public phase - countdown to end (if there's an end date)
      if (endAt) {
        const end = new Date(endAt);
        if (now < end) {
          return { isLive: true, countdownTo: end, label: 'Mint ends in' };
        }
      }
    }
    // Phase is active but no countdown needed
    return { isLive: true, countdownTo: null, label: 'Live now' };
  }

  // Not in an active phase - show when next phase STARTS
  if (allowlistEnabled && allowlistStart) {
    const alStart = new Date(allowlistStart);
    if (now < alStart) {
      return { isLive: false, countdownTo: alStart, label: allowlist2Enabled ? 'Phase 1 starts in' : 'Allowlist starts in' };
    }
  }

  if (allowlist2Enabled && allowlist2Start) {
    const al2Start = new Date(allowlist2Start);
    if (now < al2Start) {
      return { isLive: false, countdownTo: al2Start, label: 'Phase 2 starts in' };
    }
  }

  if (publicStart) {
    const pubStart = new Date(publicStart);
    if (now < pubStart) {
      return { isLive: false, countdownTo: pubStart, label: 'Public mint starts in' };
    }
  }

  return { isLive: false, countdownTo: null, label: '' };
}

export const MintSection: React.FC<MintSectionProps> = ({
  launch,
  isAuthenticated,
  quantity,
  onQuantityChange,
  mintLoading,
  mintError,
  onMint,
  pendingMint,
  pendingMintCount,
  resumeLoading,
  onResume,
  onOpenCancelModal,
  showPaymentModal,
  paymentPayload,
  onClosePaymentModal,
  onPaymentSuccess,
  onPaymentRejected,
  onPaymentExpired,
  mintStatus,
  showMintStatus,
  onCloseMintStatus,
  currentMintCount,
  showCancelModal,
  cancelCountdown,
  cancelLoading,
  onCloseCancelModal,
  onCancel,
  collectionSlug,
  compact = false,
  referral,
  editions,
  selectedEditionId = null,
  onSelectEdition,
}) => {
  const phaseInfo = getPhaseCountdownInfo(
    launch.allowlist_start_at,
    launch.public_start_at,
    launch.allowlist_enabled,
    launch.allowlist_2_start_at,
    launch.allowlist_2_enabled,
    launch.mint_phase,
    launch.end_at
  );

  // Edition drops: buyers pick a specific artwork before minting
  const isEditionLaunch = launch.collection_type === 'edition';
  const selectedEdition = isEditionLaunch && editions
    ? editions.find((e) => e.id === selectedEditionId) ?? null
    : null;

  // Cheapest still-available edition price — shown as "From X" before a pick,
  // since the launch phase price isn't any single edition's actual price.
  const editionFromPriceDrops = isEditionLaunch && editions && editions.length > 0
    ? Math.min(...(editions.some((e) => !e.sold_out)
        ? editions.filter((e) => !e.sold_out)
        : editions
      ).map((e) => e.effective_price_drops))
    : null;

  // Determine current price based on phase
  const phasePrice =
    launch.mint_phase === 'allowlist' && launch.allowlist_mint_price_drops !== null
      ? launch.allowlist_mint_price_drops
      : launch.mint_phase === 'allowlist_2' && launch.allowlist_2_mint_price_drops !== null
        ? launch.allowlist_2_mint_price_drops
        : launch.mint_price_drops;

  // An edition's own price (when set) applies in all phases
  const currentPrice = selectedEdition
    ? selectedEdition.effective_price_drops
    : phasePrice;

  const totalPrice = (currentPrice || 0) * quantity;
  const totalPriceXrp = totalPrice / 1_000_000;

  // Service fee calculation
  const serviceFeePercent = launch.service_fee_percent ?? 0;
  const perMintFee = calculateMintServiceFee(currentPrice || 0, serviceFeePercent);
  const totalFee = perMintFee * quantity;
  const totalWithFee = totalPrice + totalFee;
  const totalWithFeeXrp = totalWithFee / 1_000_000;

  // Compute savings vs public price for allowlist phases
  // (not shown when an edition sets its own flat price)
  const publicPrice = launch.mint_price_drops;
  const showSavings =
    !selectedEdition &&
    launch.mint_phase !== 'public' &&
    launch.mint_phase !== null &&
    currentPrice !== null &&
    publicPrice !== null &&
    currentPrice < publicPrice;
  const savingsPercent = showSavings
    ? Math.round(((publicPrice! - currentPrice!) / publicPrice!) * 100)
    : 0;

  // Use eligibility from launch data if available
  const eligibility = launch.eligibility;

  const baseMaxQty = eligibility?.maxAllowed ? Math.min(eligibility.maxAllowed, 10) : 1;
  // Edition drops: also capped by the selected edition's remaining copies
  const maxQty = selectedEdition
    ? Math.max(1, Math.min(baseMaxQty, selectedEdition.available_count))
    : baseMaxQty;

  // Can mint check (edition drops also require a selected artwork, and the
  // quantity must be within the (possibly edition-capped) max so a stale
  // quantity left over from a previous edition can't be submitted)
  const canMint = isAuthenticated &&
    launch.launch_status === 'active' &&
    launch.mint_phase &&
    eligibility?.eligible === true &&
    quantity <= maxQty &&
    (!isEditionLaunch || (!!selectedEdition && !selectedEdition.sold_out));

  // Reset quantity to 1 when switching artworks so a value above the new
  // edition's remaining supply can't linger in the input.
  const handleSelectEdition = (id: string | null) => {
    onSelectEdition?.(id);
    onQuantityChange(1);
  };

  // Modals (shared between both layouts)
  const modals = (
    <>
      {/* Payment Modal */}
      {showPaymentModal && paymentPayload && (
        <WalletSigningModal
          open={showPaymentModal}
          onClose={onClosePaymentModal}
          title={totalPriceXrp === 0 ? 'Confirm Free Mint' : 'Complete Mint Payment'}
          payload={paymentPayload}
          message={totalPriceXrp === 0
            ? `Confirm to mint ${quantity} NFT${quantity > 1 ? 's' : ''} for free`
            : `Pay ${totalWithFeeXrp.toLocaleString()} XRP to mint ${quantity} NFT${quantity > 1 ? 's' : ''}`}
          onSuccess={onPaymentSuccess}
          onRejected={onPaymentRejected}
          onExpired={onPaymentExpired}
        >
          {totalFee > 0 && (
            <div className="w-full border border-neutral-border rounded-lg p-4 text-body">
              {quantity > 1 ? (
                <>
                  <div className="flex items-center justify-between text-subtext-color">
                    <span>Price per NFT</span>
                    <span>{((currentPrice || 0) / 1_000_000).toLocaleString()} XRP</span>
                  </div>
                  <div className="flex items-center justify-between text-subtext-color mt-1">
                    <span>Quantity</span>
                    <span>&times; {quantity}</span>
                  </div>
                  <div className="flex items-center justify-between text-subtext-color mt-1">
                    <span>Subtotal</span>
                    <span>{totalPriceXrp.toLocaleString()} XRP</span>
                  </div>
                </>
              ) : (
                <div className="flex items-center justify-between text-subtext-color">
                  <span>Mint price</span>
                  <span>{totalPriceXrp.toLocaleString()} XRP</span>
                </div>
              )}
              <div className="flex items-center justify-between text-subtext-color mt-1">
                <span>Service fee</span>
                <span>{(totalFee / 1_000_000).toLocaleString()} XRP</span>
              </div>
              <div className="w-full h-px bg-neutral-border my-2" />
              <div className="flex items-center justify-between text-default-font font-semibold">
                <span>Total</span>
                <span className="text-brand-primary">{totalWithFeeXrp.toLocaleString()} XRP</span>
              </div>
            </div>
          )}
        </WalletSigningModal>
      )}

      {/* Mint Status Modal */}
      {showMintStatus && (
        <MintStatusModal
          open={showMintStatus}
          onClose={onCloseMintStatus}
          mintStatus={mintStatus}
          quantity={currentMintCount}
          isFree={pendingMint ? pendingMint.mint_price_drops === 0 : totalPriceXrp === 0}
        />
      )}

      {/* Cancel Confirmation Modal */}
      {showCancelModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center">
          <div
            className="absolute inset-0 bg-black/60"
            onClick={() => !cancelLoading && onCloseCancelModal()}
          />
          <div className="relative bg-default-background border border-neutral-border rounded-xl p-6 max-w-md mx-4 shadow-xl">
            <h3 className="text-heading-3 font-heading-3 text-default-font mb-4">
              Cancel Reservation?
            </h3>
            <div className="space-y-4">
              {pendingMint && pendingMint.mint_price_drops > 0 ? (
                <>
                  <p className="text-body text-subtext-color">
                    If you recently signed a payment in your wallet, please wait before canceling to avoid losing funds.
                  </p>
                  <p className="text-body text-subtext-color">
                    We'll check the XRPL for any payments before canceling. If a payment is found, your mint will continue processing.
                  </p>
                </>
              ) : (
                <p className="text-body text-subtext-color">
                  This will release your reserved NFT{pendingMintCount > 1 ? 's' : ''} and they will become available for others to mint.
                </p>
              )}
              <div className="flex gap-3 pt-2">
                <Button
                  variant="neutral-secondary"
                  size="medium"
                  onClick={onCloseCancelModal}
                  disabled={cancelLoading}
                  className="flex-1"
                >
                  Keep Reservation
                </Button>
                <Button
                  variant="destructive-primary"
                  size="medium"
                  onClick={onCancel}
                  disabled={cancelCountdown > 0 || cancelLoading}
                  loading={cancelLoading}
                  className="flex-1"
                >
                  {cancelCountdown > 0 ? `Cancel (${cancelCountdown}s)` : 'Confirm Cancel'}
                </Button>
              </div>
            </div>
          </div>
        </div>
      )}
    </>
  );

  // Claim-only drops have no public mint path at all: the only way to get one is
  // to redeem a per-copy claim link. Showing price/quantity/mint controls
  // here would just be a button that always 403s, so replace the whole mint UI
  // with an explanation. (The backend rejects these mints independently — this
  // is the story, not the security.)
  if (launch.claim_only) {
    const claimedCount = launch.progress
      ? launch.progress.minted + launch.progress.processing
      : launch.minted_count;
    const totalSupply = launch.progress?.total ?? launch.launch_total_supply ?? 0;

    return (
      <div
        className={
          compact
            ? 'w-full p-4 rounded-lg border border-neutral-border bg-default-background mb-4'
            : 'p-4 rounded-lg border border-brand-200 bg-brand-50 dark:border-brand-800 dark:bg-brand-900/20'
        }
      >
        <div className="flex items-start gap-3">
          <FeatherCreditCard className="w-5 h-5 text-brand-primary flex-shrink-0 mt-0.5" />
          <div className="flex flex-col gap-1">
            <span className="text-body-bold font-body-bold text-default-font">
              Claim code required
            </span>
            <span className="text-body font-body text-subtext-color">
              This collection can't be minted here. Each piece is unlocked by its own claim link
              from the creator — open the link you were given to claim that specific NFT.
            </span>
            {totalSupply > 0 && (
              <span className="text-caption font-caption text-subtext-color mt-1">
                {claimedCount} of {totalSupply} claimed
              </span>
            )}
          </div>
        </div>
      </div>
    );
  }

  // Compact horizontal banner layout for collection pages
  if (compact) {
    // Use progress data if available, otherwise fall back to minted_count
    // "Claimed" = minted + processing (items no longer available)
    const claimedCount = launch.progress
      ? launch.progress.minted + launch.progress.processing
      : launch.minted_count;
    const totalSupply = launch.progress?.total ?? launch.launch_total_supply ?? 0;
    const progressPercent = totalSupply > 0
      ? Math.round((claimedCount / totalSupply) * 100)
      : 0;

    return (
      <div className="w-full p-4 rounded-lg border border-neutral-border bg-default-background mb-4">
        {/* Top row: Status badges + Progress stats */}
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-2 mb-3">
          {/* Status badges */}
          <div className="flex items-center gap-2 flex-wrap">
            {launch.launch_status === 'active' ? (
              <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full bg-success-500 text-white text-caption font-caption font-semibold">
                <span className="w-1.5 h-1.5 rounded-full bg-white animate-pulse" />
                Live
              </span>
            ) : launch.launch_status === 'paused' ? (
              <span className="inline-flex items-center px-2.5 py-1 rounded-full bg-warning-500 text-white text-caption font-caption font-semibold">
                Paused
              </span>
            ) : launch.launch_status === 'completed' ? (
              <span className="inline-flex items-center px-2.5 py-1 rounded-full bg-neutral-500 text-white text-caption font-caption font-semibold">
                Sold Out
              </span>
            ) : null}

            {launch.launch_status === 'active' && launch.mint_phase && (
              <span className={`inline-flex items-center px-2.5 py-1 rounded-full text-caption font-caption font-semibold ${
                launch.mint_phase === 'allowlist'
                  ? 'bg-brand-500 text-white'
                  : launch.mint_phase === 'allowlist_2'
                    ? 'bg-brand-600 text-white'
                    : 'bg-neutral-700 dark:bg-neutral-600 text-white dark:text-neutral-100'
              }`}>
                {launch.mint_phase === 'allowlist' ? 'Allowlist Phase 1' :
                 launch.mint_phase === 'allowlist_2' ? 'Allowlist Phase 2' : 'Public Mint'}
              </span>
            )}
          </div>

          {/* Progress stats */}
          <div className="text-body text-subtext-color">
            <span className="text-default-font font-semibold">{claimedCount.toLocaleString()}</span>
            <span className="text-subtext-color"> / {totalSupply.toLocaleString()}</span>
            <span className="text-subtext-color ml-1">({progressPercent}%)</span>
          </div>
        </div>

        {/* Segmented progress bar: green for minted, yellow for processing */}
        <div className="relative h-2.5 bg-neutral-200 dark:bg-neutral-700 rounded-full overflow-hidden mb-3">
          {launch.progress ? (
            <>
              {/* Minted segment (green) */}
              {launch.progress.minted > 0 && (
                <div
                  className="absolute inset-y-0 left-0 bg-success-500 transition-all duration-500"
                  style={{ width: `${(launch.progress.minted / totalSupply) * 100}%` }}
                />
              )}
              {/* Processing/reserved segment (yellow) */}
              {launch.progress.processing > 0 && (
                <div
                  className="absolute inset-y-0 bg-warning-500 transition-all duration-500"
                  style={{
                    left: `${(launch.progress.minted / totalSupply) * 100}%`,
                    width: `${(launch.progress.processing / totalSupply) * 100}%`,
                  }}
                />
              )}
            </>
          ) : (
            <div
              className="absolute inset-y-0 left-0 rounded-full bg-gradient-to-r from-brand-400 to-brand-600 transition-all duration-500"
              style={{ width: `${progressPercent}%` }}
            />
          )}
        </div>

        {/* Detailed progress breakdown */}
        {launch.progress && (
          <div className="flex items-center justify-center gap-4 text-caption text-subtext-color mb-4">
            <span className="flex items-center gap-1.5">
              <span className="w-2 h-2 rounded-full bg-success-500" />
              {launch.progress.minted.toLocaleString()} minted
            </span>
            {launch.progress.processing > 0 && (
              <span className="flex items-center gap-1.5">
                <span className="w-2 h-2 rounded-full bg-warning-500 animate-pulse" />
                {launch.progress.processing.toLocaleString()} reserved
              </span>
            )}
            <span className="flex items-center gap-1.5">
              <span className="w-2 h-2 rounded-full bg-neutral-400" />
              {launch.progress.available.toLocaleString()} available
            </span>
          </div>
        )}

        {/* Countdown - different styling for "starts in" vs "ends in" */}
        {phaseInfo.countdownTo && (
          <div className={`p-4 rounded-lg border mb-4 ${
            phaseInfo.isLive
              ? 'border-success-500 bg-success-500/10'
              : 'border-brand-primary/20 bg-brand-primary/5'
          }`}>
            {phaseInfo.isLive && (
              <div className="flex items-center justify-center gap-2 mb-2">
                <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full bg-success-500 text-white text-caption font-caption font-semibold">
                  <span className="w-1.5 h-1.5 rounded-full bg-white animate-pulse" />
                  Minting Live
                </span>
              </div>
            )}
            <MintCountdownTimer
              targetDate={phaseInfo.countdownTo}
              label={phaseInfo.label}
            />
          </div>
        )}

        {/* Edition picker FIRST for edition drops, so the flow reads top-to-bottom:
            pick an artwork → see its price → mint (instead of forcing a scroll back up). */}
        {isEditionLaunch && editions && editions.length > 0 && !pendingMint && (
          <div className="mb-4">
            <EditionGallery
              editions={editions}
              selectedEditionId={selectedEditionId}
              onSelectEdition={handleSelectEdition}
              disabled={mintLoading}
            />
          </div>
        )}

        {/* Main content: Price + Mint controls */}
        {launch.launch_status === 'active' && launch.mint_phase && (
          <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
            {/* Price */}
            <div>
              <div className="flex items-baseline gap-2">
                <span className="text-heading-2 font-heading-2 text-default-font">
                  {isEditionLaunch && !selectedEdition && editionFromPriceDrops !== null
                    ? (editionFromPriceDrops === 0 ? 'Free' : `From ${formatDropsToXrp(editionFromPriceDrops)}`)
                    : formatDropsToXrp(currentPrice)}
                </span>
                <span className="text-caption text-subtext-color">per NFT</span>
              </div>
              {showSavings && (
                <div className="flex items-center gap-2 text-caption mt-0.5">
                  <span className="line-through text-subtext-color">
                    {formatDropsToXrp(publicPrice)}
                  </span>
                  <span className="px-1.5 py-0.5 rounded bg-success-500/15 text-success-600 dark:text-success-400 font-medium">
                    Save {savingsPercent}%
                  </span>
                </div>
              )}
            </div>

            {/* Mint controls */}
            <div className="flex items-center gap-3">
              {!isAuthenticated ? (
                <span className="text-body text-subtext-color">
                  Connect wallet to mint
                </span>
              ) : pendingMint ? (
                <>
                  <div className="flex items-center gap-2">
                    <span className="text-body text-warning-500 font-semibold flex items-center gap-1.5">
                      <FeatherClock className="w-4 h-4" />
                      {pendingMintCount > 1 ? `${pendingMintCount} NFTs` : '1 NFT'} reserved
                    </span>
                    <span className="text-caption text-subtext-color">
                      ({pendingMint.mint_price_drops === 0
                        ? 'Free'
                        : `${((pendingMint.mint_price_drops * pendingMintCount) / 1_000_000).toLocaleString()} XRP`})
                    </span>
                  </div>
                  <Button
                    variant="brand-primary"
                    size="medium"
                    onClick={onResume}
                    disabled={resumeLoading}
                    loading={resumeLoading}
                  >
                    Resume
                  </Button>
                  <Button
                    variant="neutral-secondary"
                    size="medium"
                    onClick={onOpenCancelModal}
                    disabled={resumeLoading}
                  >
                    Cancel
                  </Button>
                </>
              ) : isEditionLaunch && !selectedEdition ? (
                <span className="text-body text-subtext-color flex items-center gap-1.5">
                  <FeatherAlertCircle className="w-4 h-4" />
                  Pick an artwork above to mint
                </span>
              ) : eligibility?.eligible && eligibility.maxAllowed > 0 ? (
                <>
                  {/* Max eligibility hint */}
                  <span className="text-caption text-subtext-color hidden sm:inline">
                    Max {eligibility.maxAllowed}
                  </span>

                  {/* Quantity selector */}
                  <div className="flex items-center border border-neutral-border rounded-lg overflow-hidden bg-default-background">
                    <button
                      type="button"
                      onClick={() => onQuantityChange(Math.max(1, quantity - 1))}
                      disabled={quantity <= 1}
                      className="w-9 h-9 flex items-center justify-center hover:bg-neutral-200 disabled:opacity-40 disabled:cursor-not-allowed transition-colors text-default-font"
                    >
                      <span className="text-lg font-medium leading-none">−</span>
                    </button>
                    <input
                      type="number"
                      min={1}
                      max={maxQty}
                      value={quantity}
                      onChange={(e) => {
                        const val = parseInt(e.target.value, 10);
                        if (!isNaN(val)) {
                          onQuantityChange(Math.max(1, Math.min(val, maxQty)));
                        }
                      }}
                      onWheel={(e) => e.currentTarget.blur()}
                      className="w-10 h-9 text-center text-body-bold font-body-bold text-default-font bg-transparent border-x border-neutral-border focus:outline-none [appearance:textfield] [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none"
                    />
                    <button
                      type="button"
                      onClick={() => onQuantityChange(Math.min(maxQty, quantity + 1))}
                      disabled={quantity >= maxQty}
                      className="w-9 h-9 flex items-center justify-center hover:bg-neutral-200 disabled:opacity-40 disabled:cursor-not-allowed transition-colors text-default-font"
                    >
                      <span className="text-lg font-medium leading-none">+</span>
                    </button>
                  </div>

                  {/* Total */}
                  <div className="flex items-center gap-1.5">
                    <span className="text-caption text-subtext-color">Total:</span>
                    <span className="text-body-bold font-body-bold text-default-font">
                      {totalPriceXrp === 0 ? 'Free' : `${totalPriceXrp.toLocaleString()} XRP`}
                    </span>
                  </div>

                  {/* Mint button */}
                  <Button
                    variant="brand-primary"
                    size="medium"
                    onClick={onMint}
                    disabled={!canMint || mintLoading}
                    loading={mintLoading}
                  >
                    Mint Now
                  </Button>
                </>
              ) : eligibility ? (
                <span className="text-body text-error-500 flex items-center gap-1.5">
                  <FeatherAlertCircle className="w-4 h-4" />
                  {eligibility.reason || 'Not eligible'}
                </span>
              ) : (
                <span className="text-body text-subtext-color flex items-center gap-1.5">
                  <LoadingSpinner size="sm" />
                  Checking eligibility...
                </span>
              )}
            </div>
          </div>
        )}

        {/* Error display */}
        {mintError && (
          <div className="mt-3 p-3 rounded-lg bg-error-50 dark:bg-error-900/40 border border-error-200 dark:border-error-700">
            <p className="text-body text-error-700 dark:text-error-200">
              {mintError}
            </p>
          </div>
        )}

        {/* Inline Share & Earn referral row */}
        {referral && <ShareAndEarnInline {...referral} />}

        {modals}
      </div>
    );
  }

  // Full vertical layout
  // Status badges section
  const statusBadges = (
    <div className="flex items-center gap-2 mb-3">
      {launch.launch_status === 'active' ? (
        <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full bg-success-500 text-white text-caption font-caption">
          <span className="w-1.5 h-1.5 rounded-full bg-white animate-pulse" />
          Live
        </span>
      ) : launch.launch_status === 'paused' ? (
        <span className="inline-flex items-center px-2.5 py-1 rounded-full bg-warning-500 text-white text-caption font-caption">
          Paused
        </span>
      ) : launch.launch_status === 'completed' ? (
        <span className="inline-flex items-center px-2.5 py-1 rounded-full bg-neutral-500 text-white text-caption font-caption">
          Sold Out
        </span>
      ) : null}

      {launch.launch_status === 'active' && launch.mint_phase && (
        <span className={`inline-flex items-center px-2.5 py-1 rounded-full text-caption font-caption font-semibold ${
          launch.mint_phase === 'allowlist' || launch.mint_phase === 'allowlist_2'
            ? 'bg-brand-500 text-white'
            : 'bg-black text-white'
        }`}>
          {launch.mint_phase === 'allowlist' ? 'Allowlist Phase 1' :
           launch.mint_phase === 'allowlist_2' ? 'Allowlist Phase 2' : 'Public Mint'}
        </span>
      )}
    </div>
  );

  return (
    <div className="p-4 rounded-lg border border-brand-200 bg-brand-50 dark:border-brand-800 dark:bg-brand-900/20">
      {/* Status badges */}
      {statusBadges}

      {/* Progress */}
      <MintProgressBar
        mintedCount={launch.minted_count}
        totalSupply={launch.launch_total_supply}
        className="mb-4"
      />

      {/* Price Info */}
      <div className="p-3 rounded-lg border border-neutral-border bg-default-background mb-4">
        <div className="flex items-center justify-between mb-2">
          <span className="text-body text-subtext-color">Price per NFT</span>
          <div className="flex flex-col items-end">
            <span className="text-heading-3 font-heading-3 text-default-font">
              {isEditionLaunch && !selectedEdition && editionFromPriceDrops !== null
                ? (editionFromPriceDrops === 0 ? 'Free' : `From ${formatDropsToXrp(editionFromPriceDrops)}`)
                : formatDropsToXrp(currentPrice)}
            </span>
            {showSavings && (
              <div className="flex items-center gap-1.5 text-caption mt-0.5">
                <span className="line-through text-subtext-color">
                  {formatDropsToXrp(publicPrice)}
                </span>
                <span className="px-1.5 py-0.5 rounded bg-success-500/15 text-success-600 dark:text-success-400 font-medium">
                  {savingsPercent}% off
                </span>
              </div>
            )}
          </div>
        </div>

        {launch.transfer_fee && launch.transfer_fee > 0 && (
          <div className="flex items-center justify-between text-caption text-subtext-color">
            <span>Royalty</span>
            <span>{formatRoyalty(launch.transfer_fee)}%</span>
          </div>
        )}

      </div>

      {/* Countdown - shows "starts in" when not live, "ends in" when live */}
      {phaseInfo.countdownTo && (
        <div className={`p-4 rounded-lg border mb-4 ${
          phaseInfo.isLive
            ? 'border-success-500 bg-success-500/10'
            : 'border-brand-primary/20 bg-brand-primary/5'
        }`}>
          {phaseInfo.isLive && (
            <div className="flex items-center justify-center gap-2 mb-2">
              <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full bg-success-500 text-white text-caption font-caption font-semibold">
                <span className="w-1.5 h-1.5 rounded-full bg-white animate-pulse" />
                Minting Live
              </span>
            </div>
          )}
          <MintCountdownTimer
            targetDate={phaseInfo.countdownTo}
            label={phaseInfo.label}
          />
          {/* Allowlist status badges (only when not yet started) */}
          {!phaseInfo.isLive && (launch.allowlist_enabled || launch.allowlist_2_enabled) && launch.userAllowlistStatus && (
            <div className="flex flex-wrap justify-center gap-2 mt-3">
              {launch.allowlist_enabled && (
                <span className={`inline-block px-3 py-1.5 rounded-full text-body-bold font-body-bold ${
                  launch.userAllowlistStatus.isOnAllowlist
                    ? 'bg-success-500 text-white'
                    : 'bg-neutral-500 text-white'
                }`}>
                  {launch.allowlist_2_enabled ? 'Phase 1: ' : ''}
                  {launch.userAllowlistStatus.isOnAllowlist ? 'On Allowlist' : 'Not on Allowlist'}
                </span>
              )}
              {launch.allowlist_2_enabled && (
                <span className={`inline-block px-3 py-1.5 rounded-full text-body-bold font-body-bold ${
                  launch.userAllowlistStatus.isOnAllowlist2
                    ? 'bg-success-500 text-white'
                    : 'bg-neutral-500 text-white'
                }`}>
                  Phase 2: {launch.userAllowlistStatus.isOnAllowlist2 ? 'On Allowlist' : 'Not on Allowlist'}
                </span>
              )}
            </div>
          )}
        </div>
      )}

      {/* Eligibility / Mint Section */}
      {launch.launch_status === 'active' && launch.mint_phase && (
        <div className="p-3 rounded-lg border border-neutral-border bg-default-background">
          {!isAuthenticated ? (
            <div className="text-center py-4">
              <p className="text-body text-subtext-color">
                Connect your wallet to mint
              </p>
            </div>
          ) : eligibility ? (
            <>
              {/* Pending Mint UI */}
              {pendingMint ? (
                <div className="space-y-3">
                  <div className="flex items-center gap-3 p-3 rounded-lg bg-warning-500/20 border border-warning-500">
                    <FeatherClock className="w-5 h-5 text-warning-500 flex-shrink-0" />
                    <div className="flex-1">
                      <span className="text-body font-semibold text-default-font block">
                        {pendingMintCount > 1
                          ? `You have ${pendingMintCount} NFTs reserved`
                          : 'You have 1 NFT reserved'}
                      </span>
                      <span className="text-caption text-subtext-color">
                        {pendingMint.mint_price_drops === 0
                          ? 'Free mint'
                          : `Total: ${((pendingMint.mint_price_drops * pendingMintCount) / 1_000_000).toLocaleString()} XRP`}
                      </span>
                    </div>
                  </div>

                  <p className="text-body text-subtext-color">
                    {pendingMint.mint_price_drops === 0
                      ? 'Confirm in your wallet to finish minting, or cancel to start over.'
                      : 'Complete your payment to finish minting, or cancel to start over.'}
                  </p>

                  {mintError && (
                    <div className="p-3 rounded-lg bg-error-50 dark:bg-error-900/40 border border-error-200 dark:border-error-700">
                      <p className="text-body text-error-700 dark:text-error-200">
                        {mintError}
                      </p>
                    </div>
                  )}

                  <div className="flex gap-3">
                    <Button
                      variant="brand-primary"
                      size="medium"
                      onClick={onResume}
                      disabled={resumeLoading}
                      loading={resumeLoading}
                      className="flex-1"
                    >
                      {pendingMint.mint_price_drops === 0 ? 'Resume Mint' : 'Resume Payment'}
                    </Button>
                    <Button
                      variant="neutral-secondary"
                      size="medium"
                      onClick={onOpenCancelModal}
                      disabled={resumeLoading}
                      className="flex-1"
                    >
                      Cancel
                    </Button>
                  </div>
                </div>
              ) : (
                <>
                  {/* Eligibility Status */}
                  {!eligibility.eligible ? (
                    <div className="flex items-center gap-3 p-3 rounded-lg bg-error-500 mb-3">
                      <FeatherAlertCircle className="w-5 h-5 text-white flex-shrink-0" />
                      <span className="text-body font-semibold text-white">
                        {eligibility.reason || 'Not eligible to mint'}
                      </span>
                    </div>
                  ) : (
                    <div className="flex items-center gap-3 p-3 rounded-lg bg-success-500 mb-3">
                      <FeatherCheck className="w-5 h-5 text-white flex-shrink-0" />
                      <span className="text-body font-semibold text-white">
                        You can mint up to {eligibility.maxAllowed} NFT{eligibility.maxAllowed !== 1 ? 's' : ''}
                      </span>
                    </div>
                  )}

                  {/* Edition picker (edition drops only) */}
                  {isEditionLaunch && editions && editions.length > 0 && (
                    <div className="mb-4">
                      <EditionGallery
                        editions={editions}
                        selectedEditionId={selectedEditionId}
                        onSelectEdition={handleSelectEdition}
                        disabled={mintLoading}
                      />
                    </div>
                  )}

                  {/* Quantity Selector */}
                  {eligibility.eligible && eligibility.maxAllowed > 0 && (
                    <>
                      <div className="flex items-center justify-between mb-3">
                        <span className="text-body text-default-font">Quantity</span>
                        <input
                          type="number"
                          min={1}
                          max={maxQty}
                          step={1}
                          value={quantity}
                          onChange={(e) => {
                            const val = parseInt(e.target.value, 10);
                            if (!isNaN(val)) {
                              onQuantityChange(Math.max(1, Math.min(val, maxQty)));
                            }
                          }}
                          className="w-20 px-3 py-2 text-center text-body-bold font-body-bold text-default-font bg-default-background border border-neutral-border rounded-lg focus:outline-none focus:border-brand-500 [appearance:textfield] [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none"
                        />
                      </div>

                      {/* Total Price */}
                      <div className="flex items-center justify-between mb-3 pb-3 border-b border-neutral-border">
                        <span className="text-body text-subtext-color">Total</span>
                        <span className="text-heading-3 font-heading-3 text-default-font">
                          {totalPriceXrp === 0 ? 'Free' : `${totalPriceXrp.toLocaleString()} XRP`}
                        </span>
                      </div>

                      {/* Error Display */}
                      {mintError && (
                        <div className="p-3 rounded-lg bg-error-50 dark:bg-error-900/40 border border-error-200 dark:border-error-700 mb-3">
                          <p className="text-body text-error-700 dark:text-error-200">
                            {mintError}
                          </p>
                        </div>
                      )}

                      {/* Mint Button */}
                      <Button
                        variant="brand-primary"
                        size="large"
                        onClick={onMint}
                        disabled={!canMint || mintLoading}
                        loading={mintLoading}
                        className="w-full"
                      >
                        {isEditionLaunch && !selectedEdition
                          ? 'Select an Artwork to Mint'
                          : totalPriceXrp === 0
                            ? 'Mint for Free'
                            : `Mint for ${totalPriceXrp.toLocaleString()} XRP`}
                      </Button>
                    </>
                  )}
                </>
              )}
            </>
          ) : (
            <div className="flex items-center justify-center py-4">
              <LoadingSpinner size="sm" />
              <span className="ml-2 text-body text-subtext-color">
                Checking eligibility...
              </span>
            </div>
          )}
        </div>
      )}

      {/* View Collection Link (if on drop page and collection exists) */}
      {collectionSlug && (
        <Link
          to={`/collections/${collectionSlug}`}
          className="flex items-center justify-center gap-2 w-full py-3 px-4 mt-4 rounded-lg border border-neutral-border bg-default-background text-body-bold font-body-bold text-default-font hover:border-brand-500 transition-colors"
        >
          View Collection
        </Link>
      )}

      {/* Inline Share & Earn referral row */}
      {referral && <ShareAndEarnInline {...referral} />}

      {modals}
    </div>
  );
};

/**
 * Compact inline Share & Earn row for inside the mint box
 */
function ShareAndEarnInline({
  launchSlug,
  referralCode,
  referralBps,
  mintPriceDrops,
  serviceFeePercent,
}: {
  launchSlug: string;
  referralCode: string;
  referralBps: number;
  mintPriceDrops: number;
  serviceFeePercent: number;
}) {
  const [copied, setCopied] = useState(false);
  const referralUrl = `${window.location.origin}/drops/${launchSlug}?mref=${referralCode}`;
  const rewardPercent = referralBps / 100;
  // mintPriceDrops IS the creator's share — platform fee is added on top for the buyer
  const perMintReward = Math.floor(mintPriceDrops * referralBps / 10000);

  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(referralUrl);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch { /* ignore */ }
  };

  return (
    <div className="mt-3 pt-3 border-t border-neutral-border">
      <div className="flex items-center gap-3 flex-wrap">
        <span className="text-caption font-caption text-subtext-color whitespace-nowrap">
          Share & Earn {rewardPercent}%
          {mintPriceDrops > 0 && perMintReward > 0 && (
            <span className="ml-1 text-brand-600">
              ({parseFloat((perMintReward / 1_000_000).toFixed(6))} XRP/mint)
            </span>
          )}
        </span>
        <div className="flex items-center gap-1.5 flex-1 min-w-0">
          <input
            type="text"
            readOnly
            value={referralUrl}
            className="flex-1 min-w-0 px-2 py-1 rounded border border-neutral-border bg-neutral-100 text-caption font-caption text-subtext-color truncate"
          />
          <button
            onClick={handleCopy}
            className="flex items-center gap-1 px-2 py-1 rounded border border-neutral-border text-caption font-caption text-default-font hover:bg-neutral-100 transition-colors shrink-0"
          >
            {copied ? (
              <FeatherCheck className="w-3.5 h-3.5 text-success-600" />
            ) : (
              <FeatherCopy className="w-3.5 h-3.5" />
            )}
            {copied ? 'Copied' : 'Copy'}
          </button>
        </div>
      </div>
    </div>
  );
}
